import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import StudentsPanel from "./StudentsPanel";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function ClassListItem({ c, selected, confirmDelete, onSelect, onEdit, onAskDelete, onConfirmDelete }) {
  return (
    <div
      className={`py-3 px-2 flex items-center justify-between gap-2 cursor-pointer transition-colors ${selected ? "bg-[var(--parchment)]/70" : "hover:bg-[var(--parchment)]/40"}`}
      onClick={onSelect}
      data-testid={`class-row-${c.id}`}
    >
      <div>
        <div className="font-medium">{c.name}</div>
        <div className="text-xs text-[var(--ink-soft)]">{c.level} · {c.student_count} élève{c.student_count > 1 ? "s" : ""}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          className="btn-secondary !py-1 !px-2 text-xs"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`edit-class-${c.id}`}
        >
          <Pencil size={13} />
        </button>
        {confirmDelete ? (
          <button
            className="!py-1 !px-2 text-xs bg-[var(--late)] text-white"
            onClick={(e) => { e.stopPropagation(); onConfirmDelete(); }}
            data-testid={`confirm-delete-class-${c.id}`}
          >
            Confirmer ?
          </button>
        ) : (
          <button
            className="btn-secondary !py-1 !px-2 text-xs !text-[var(--late)]"
            onClick={(e) => { e.stopPropagation(); onAskDelete(); }}
            data-testid={`delete-class-${c.id}`}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function ClassFormDialog({ dialog, setDialog, onSave }) {
  return (
    <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            {dialog?.mode === "create" ? "Nouvelle classe" : "Modifier la classe"}
          </DialogTitle>
        </DialogHeader>
        {dialog && (
          <div className="space-y-3">
            <div>
              <label className="eyebrow block mb-1">Nom</label>
              <input className={inputCls} value={dialog.name} onChange={(e) => setDialog((s) => ({ ...s, name: e.target.value }))} placeholder="Ex. 6ème B" data-testid="class-name-input" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Niveau</label>
              <select className={inputCls} value={dialog.level} onChange={(e) => setDialog((s) => ({ ...s, level: e.target.value }))} data-testid="class-level-select">
                <option value="Collège">Collège</option>
                <option value="Lycée">Lycée</option>
              </select>
            </div>
          </div>
        )}
        <DialogFooter>
          <button className="btn-secondary" onClick={() => setDialog(null)}>Annuler</button>
          <button className="btn-primary" onClick={onSave} data-testid="class-save-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClassListColumn({ classes, loading, selected, confirmId, onAdd, onSelect, onEdit, onAskDelete, onConfirmDelete }) {
  return (
    <div className="lg:col-span-2 bg-white border border-[var(--parchment-2)] p-6">
      <div className="flex items-center justify-between pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Classes</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">{classes.length} classe{classes.length > 1 ? "s" : ""}</h2>
        </div>
        <button
          className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
          onClick={onAdd}
          data-testid="add-class-btn"
        >
          <Plus size={15} /> Classe
        </button>
      </div>
      {loading ? (
        <div className="mt-4 text-[var(--ink-soft)]">Chargement...</div>
      ) : (
        <div className="mt-3 divide-y divide-[var(--parchment-2)]" data-testid="classes-list">
          {classes.map((c) => (
            <ClassListItem
              key={c.id}
              c={c}
              selected={selected === c.id}
              confirmDelete={confirmId === c.id}
              onSelect={() => onSelect(c.id)}
              onEdit={() => onEdit(c)}
              onAskDelete={() => onAskDelete(c.id)}
              onConfirmDelete={() => onConfirmDelete(c.id)}
            />
          ))}
          {classes.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucune classe.</div>}
        </div>
      )}
    </div>
  );
}

function useClassesCrud() {
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/classes");
    setClasses(data);
    setLoading(false);
    setSelected((prev) => (data.find((c) => c.id === prev) ? prev : data[0]?.id || null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!dialog.name.trim()) {
      toast.error("Nom de classe requis");
      return;
    }
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/classes", { name: dialog.name, level: dialog.level });
        toast.success("Classe créée");
      } else {
        await api.put(`/admin/classes/${dialog.id}`, { name: dialog.name, level: dialog.level });
        toast.success("Classe modifiée");
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/classes/${id}`);
      toast.success("Classe supprimée");
      setConfirmId(null);
      setSelected((prev) => (prev === id ? null : prev));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { classes, selected, setSelected, loading, dialog, setDialog, confirmId, setConfirmId, save, remove, load };
}

export default function ClassesManager() {
  const { classes, selected, setSelected, loading, dialog, setDialog, confirmId, setConfirmId, save, remove, load } = useClassesCrud();

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="classes-manager">
      <ClassListColumn
        classes={classes}
        loading={loading}
        selected={selected}
        confirmId={confirmId}
        onAdd={() => setDialog({ mode: "create", name: "", level: "Collège" })}
        onSelect={setSelected}
        onEdit={(c) => setDialog({ mode: "edit", id: c.id, name: c.name, level: c.level })}
        onAskDelete={setConfirmId}
        onConfirmDelete={remove}
      />

      <div className="lg:col-span-3">
        {selected ? (
          <StudentsPanel classId={selected} klass={classes.find((c) => c.id === selected)} onChanged={load} />
        ) : (
          <div className="bg-white border border-[var(--parchment-2)] p-6 text-[var(--ink-soft)] text-sm flex items-center gap-2">
            <Users size={16} /> Sélectionnez une classe pour gérer ses élèves.
          </div>
        )}
      </div>

      <ClassFormDialog dialog={dialog} setDialog={setDialog} onSave={save} />
    </div>
  );
}
